import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { Fragment, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui/layout/layout';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/Pagination';
import { ScrollArea } from '@/components/ui/ScrollArea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { getEnvVar } from '@/env';
import { type ApiLog } from '@/features/request-logger/api-logs.types';
import {
  ContentViewer,
  ConversationDataViewer,
  DocumentDataViewer,
  FrameStatsViewer,
  MetadataViewer,
  TraversedContentViewer,
} from '@/features/request-logger/request-logger.viewers';
import { usePagination } from '@/hooks/usePagination';
import { useApiInterceptor } from '@/providers/ApiLogsInterceptorProvider';

import { RequestLoggerTag } from './request-logger.common';

const API_URL = getEnvVar('VITE_API_URL');

export const RequestLoggerTable = () => {
  const { filteredLogs } = useApiInterceptor();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'timestamp', desc: true },
  ]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
    currentPage: pagination.pageIndex,
    totalPages: Math.ceil(filteredLogs.length / pagination.pageSize),
    paginationItemsToDisplay: 10,
  });

  const columns = useMemo<ColumnDef<ApiLog, unknown>[]>(
    () => [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) => (
          <div className="p-1">
            {row.getIsExpanded() ? (
              <Icons.chevronDown className="h-4 w-4 text-foreground-subtle" />
            ) : (
              <Icons.chevronRight className="h-4 w-4 text-foreground-subtle" />
            )}
          </div>
        ),
        size: 40,
      },
      {
        header: 'Method & Endpoint',
        accessorFn: (row) => `${row.method} ${row.url}`,
        id: 'methodEndpoint',
        cell: ({ row }) => (
          <div className="flex items-center gap-4">
            <RequestLoggerTag method={row.original.method} />
            <span className="font-mono text-xs truncate text-foreground-subtle">
              {row.original.url.startsWith(API_URL)
                ? row.original.url.replace(API_URL, '')
                : row.original.url}
            </span>
          </div>
        ),
        size: 250,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <RequestLoggerTag status={row.original.statusCode} />
        ),
        size: 80,
      },
      {
        header: 'App',
        accessorKey: 'app',
        cell: ({ row }) => <RequestLoggerTag app={row.original.app} />,
        size: 120,
      },
      {
        header: 'Tags',
        accessorKey: 'tags',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.tags.map((tag) => (
              <RequestLoggerTag
                key={`${row.original.requestId}-${tag}`}
                tag={tag}
              />
            ))}
          </div>
        ),
        size: 200,
      },
      {
        header: 'Sent at',
        accessorKey: 'timestamp',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {format(row.original.timestamp, 'HH:mm:ss')}
          </span>
        ),
        size: 80,
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredLogs,
    columns,
    state: {
      sorting,
      expanded,
      pagination,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (!filteredLogs.length) {
    return (
      <div className="text-center p-12 border border-border rounded-lg">
        <p className="text-foreground-subtle">No request logs available yet!</p>
      </div>
    );
  }

  return (
    <YStack className="flex-1 gap-4 justify-between overflow-hidden">
      <div className="max-h-full overflow-y-hidden">
        <ScrollArea className="max-h-full border rounded-md border-border">
          <Table>
            <TableHeader className="border-b border-border bg-surface ">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-none ">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={`py-4 text-foreground-subtle ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <Icons.chevronUp className="h-3 w-3 ml-1" />,
                          desc: <Icons.chevronDown className="h-3 w-3 ml-1" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const isTraversalDataRequest =
                  row.original.isTraversalDataRequest;
                const isConversationDataRequest =
                  row.original.isConversationDataRequest;
                const isTextTraversalDataFrame =
                  row.original.isTextTraversalDataFrame;
                const isDocumentDataFrame = row.original.isDocumentDataFrame;
                const isBrowserTraversalDataFrame =
                  row.original.isBrowserTraversalDataFrame;

                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className={
                        row.getIsExpanded()
                          ? 'bg-surface border-border'
                          : 'hover:bg-surface border-border'
                      }
                      onClick={() => row.toggleExpanded()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow
                        key={`${row.id}-expanded`}
                        className="bg-muted/50"
                      >
                        <TableCell colSpan={columns.length} className="p-0">
                          <div className="px-4 py-2">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <YStack className="gap-4">
                                {isTraversalDataRequest &&
                                  !isBrowserTraversalDataFrame && (
                                    <MetadataViewer
                                      window={row.original.requestBody.window}
                                      detectedApp={row.original.requestBody.app}
                                      timestamp={
                                        row.original.requestBody.timestamp
                                      }
                                      type={row.original.requestBody.type}
                                      contentType={row.original.contentType}
                                    />
                                  )}
                                {isBrowserTraversalDataFrame && (
                                  <MetadataViewer
                                    window={row.original.requestBody.window}
                                    detectedApp={row.original.requestBody.app}
                                    timestamp={
                                      row.original.requestBody.timestamp
                                    }
                                    type={row.original.requestBody.type}
                                    contentType={row.original.contentType}
                                    url={row.original.requestBody.url}
                                  />
                                )}
                                {isConversationDataRequest && (
                                  <ConversationDataViewer
                                    messages={row.original.requestBody.messages}
                                  />
                                )}
                                {isDocumentDataFrame && (
                                  <DocumentDataViewer
                                    documentData={JSON.stringify(
                                      row.original.requestBody,
                                      ['app', 'window', 'title', 'content'],
                                      2
                                    )}
                                  />
                                )}
                                {(isTextTraversalDataFrame ||
                                  isBrowserTraversalDataFrame) && (
                                  <TraversedContentViewer
                                    content={row.original.requestBody.content}
                                  />
                                )}
                              </YStack>
                              <YStack>
                                <ContentViewer
                                  content={JSON.stringify(
                                    row.original.responseBody,
                                    null,
                                    2
                                  )}
                                  title="Response Body"
                                />
                                {row.original.errorMessage && (
                                  <ContentViewer
                                    content={row.original.errorMessage}
                                    title="Error Message"
                                  />
                                )}
                                {isTraversalDataRequest && (
                                  <FrameStatsViewer
                                    data={row.original.requestBody.frame_stats}
                                  />
                                )}
                              </YStack>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
      {pages.length > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                aria-disabled={pagination.pageIndex === 0 ? true : undefined}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    pageIndex: prev.pageIndex - 1,
                  }))
                }
              />
            </PaginationItem>
            {showLeftEllipsis && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}

            {/* Page number links */}
            {pages.map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: page - 1,
                    }))
                  }
                  isActive={page - 1 === pagination.pageIndex}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}

            {showRightEllipsis && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationNext
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                aria-disabled={
                  pagination.pageIndex >=
                  Math.ceil(filteredLogs.length / pagination.pageSize) - 1
                    ? true
                    : undefined
                }
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    pageIndex: prev.pageIndex + 1,
                  }))
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </YStack>
  );
};
